<template>
  <div class="auth-screen">
    <section class="auth-shell" aria-label="Fleetwarden authentication">
        <div class="auth-brand" aria-hidden="true">
            <img alt="" src="@/assets/birdwings-logo.svg" />
            <div class="auth-wordmark">FLEET<span>WARDEN</span></div>
        </div>

        <div class="auth-stage">
            <Transition name="auth-state" mode="out-in">
                <div v-if="isloggedin" key="loading" class="auth-content auth-loading" role="status">
                    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                    <span>Opening Fleetwarden...</span>
                </div>

                <div v-else-if="m_RecoveryMode && m_RecoveryRequested" key="recovery-sent" class="auth-content auth-message">
                    <div class="auth-status-mark" aria-hidden="true">
                        <i class="pi pi-check"></i>
                    </div>
                    <h1>Check your inbox</h1>
                    <p>You will receive an email shortly with a link to recover your password.</p>
                    <button type="button" class="auth-text-action" @click="closeRecoveryForm">Back to login</button>
                </div>

                <div v-else-if="m_RecoveryMode" key="recovery" class="auth-content">
                    <button type="button" class="auth-back" :disabled="m_RecoverySubmitting" @click="closeRecoveryForm">
                        <i class="pi pi-arrow-left" aria-hidden="true"></i>
                        <span>Back to login</span>
                    </button>
                    <div class="auth-heading">
                        <h1>Recover access</h1>
                        <p>Enter your login email and we will send you a link to choose a new password.</p>
                    </div>
                    <div class="auth-field">
                        <label for="recovery-email">Login email</label>
                        <InputText id="recovery-email" autofocus v-model="m_RecoveryEmail" type="email" autocomplete="email" @keyup.enter="submitLoginRecovery" maxlength="128" />
                    </div>
                    <div class="auth-actions">
                        <Button label="Send recovery link" class="auth-primary" :loading="m_RecoverySubmitting" :disabled="m_RecoverySubmitting || m_RecoveryEmail.length < 1" @click="submitLoginRecovery" />
                    </div>
                </div>

                <div v-else-if="!hasLoginSelection" key="login" class="auth-content">
                    <div class="auth-heading">
                        <h1>Log in</h1>
                    </div>
                    <div class="auth-field">
                        <label for="login-identifier">Username or email</label>
                        <InputText id="login-identifier" autofocus v-model="m_Username" type="text" autocomplete="username" @keyup.enter="submitLogin" maxlength="128" />
                    </div>
                    <div class="auth-field">
                        <label for="login-password">Password</label>
                        <Password inputId="login-password" v-model="m_Password" :feedback="false" autocomplete="current-password" @keyup.enter="submitLogin" maxlength="64" toggleMask :inputStyle="{'width':'100%'}" />
                    </div>
                    <div class="auth-field">
                        <label for="login-otp">One-time password</label>
                        <InputText id="login-otp" v-model="m_OTP" type="text" inputmode="numeric" autocomplete="one-time-code" @keyup.enter="submitLogin" placeholder="6 digits" maxlength="8" />
                    </div>
                    <div class="auth-actions">
                        <Button label="Log in" class="auth-primary" @click="submitLogin" />
                        <button type="button" class="auth-text-action" @click="openRecoveryForm">Forgot your password?</button>
                    </div>
                </div>

                <div v-else key="chooser" class="auth-content">
                    <button type="button" class="auth-back" @click="clearPendingLogin(true)">
                        <i class="pi pi-arrow-left" aria-hidden="true"></i>
                        <span>Back to login</span>
                    </button>
                    <div class="auth-heading">
                        <h1>Choose access</h1>
                        <p>Select the account or shared devices you want to open.</p>
                    </div>
                    <div class="auth-option-list">
                        <button v-for="account in m_Accounts" :key="account.pkey" type="button" class="auth-option" @click="selectAccount(account)">
                            <span>
                                <strong>{{ account.company || ('Account ' + account.pkey) }}</strong>
                                <small>{{ formatAccountAddress(account) }}</small>
                            </span>
                            <i class="pi pi-angle-right" aria-hidden="true"></i>
                        </button>
                        <button v-if="m_SingleDevCount > 0" type="button" class="auth-option" @click="selectDeviceAccess">
                            <span>
                                <strong>Devices shared with me</strong>
                                <small>{{ m_SingleDevCount }} {{ m_SingleDevCount === 1 ? 'device' : 'devices' }}</small>
                            </span>
                            <i class="pi pi-angle-right" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </Transition>
        </div>
    </section>
  </div>
</template>

<script setup>

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import { useRestApi } from "@/RESTService";

const store = useStore();
const router = useRouter();
const restService = useRestApi();
const m_Password = ref("");
const m_Username = ref("");
const m_OTP = ref("");
const m_LoginToken = ref("");
const m_Accounts = ref([]);
const m_SingleDevCount = ref(0);
const m_RecoveryMode = ref(false);
const m_RecoveryEmail = ref("");
const m_RecoveryRequested = ref(false);
const m_RecoverySubmitting = ref(false);
let m_LoginExpireTimer = null;

const clearPendingLogin = (clearInputs = false) =>
{
    if (m_LoginExpireTimer) {
        clearTimeout(m_LoginExpireTimer);
        m_LoginExpireTimer = null;
    }
    m_LoginToken.value = "";
    m_Accounts.value = [];
    m_SingleDevCount.value = 0;
    if (clearInputs) {
        m_Password.value = "";
        m_OTP.value = "";
    }
}

const expirePendingLogin = () =>
{
    clearPendingLogin(true);
    store.commit("seterror", {severity:"warn", caption : "Login expired", message:"Please log in again.", duration: 6000} );
}

const schedulePendingLoginExpiration = () =>
{
    if (m_LoginExpireTimer) {
        clearTimeout(m_LoginExpireTimer);
    }

    // Account selection has a five-minute lifetime; do not depend on the computer's clock.
    // The API independently enforces the token's actual expiry when it is redeemed.
    m_LoginExpireTimer = setTimeout(expirePendingLogin, 300000);
};

const selectAccount = async (account) =>
{
    let loggedIn = await store.dispatch("completeLogin", {token: m_LoginToken.value, scope: "account", accountkey: account?.pkey ?? 0});
    if (loggedIn) {
        clearPendingLogin(false);
    }
}

const selectDeviceAccess = async () =>
{
    let loggedIn = await store.dispatch("completeLogin", {token: m_LoginToken.value, scope: "device-access"});
    if (loggedIn) {
        clearPendingLogin(false);
    }
}

const formatAccountAddress = (account) =>
{
    return [account?.addressline, account?.city, account?.state_region, account?.countrycode].filter((value) => value?.length > 0).join(", ");
}

const openRecoveryForm = () =>
{
    clearPendingLogin(true);
    m_RecoveryEmail.value = m_Username.value.includes("@") ? m_Username.value : "";
    m_RecoveryRequested.value = false;
    m_RecoveryMode.value = true;
}

const closeRecoveryForm = () =>
{
    m_RecoveryMode.value = false;
    m_RecoveryRequested.value = false;
    m_RecoverySubmitting.value = false;
}

const submitLoginRecovery = async () =>
{
    if (m_RecoverySubmitting.value || m_RecoveryEmail.value.length < 1) return;

    m_RecoverySubmitting.value = true;
    try {
        await restService.requestLoginRecovery(m_RecoveryEmail.value);
        m_RecoveryRequested.value = true;
    } catch {
        store.commit("seterror", {severity:"error", caption:"Recovery request failed", message:"Please try again later.", duration:6000});
    } finally {
        m_RecoverySubmitting.value = false;
    }
}

onMounted(() => {
  if (store.getters.isloggedin) {
    router.replace('/');
  }
});

onUnmounted(() => {
  clearPendingLogin(false);
});

const submitLogin = async () =>
{
    if (m_Username.value.length < 1) {
        store.commit("seterror", {severity:"warn", caption : "Missing username or email", message:"Please enter your username or email.", duration: 6000} );
    }
    else if (m_Password.value.length < 4) {
        store.commit("seterror", {severity:"warn", caption : "Password too short", message:"Please enter the complete password.", duration: 6000} );
    }
    else {
        clearPendingLogin(false);
        let cred = {username: m_Username.value, password: m_Password.value, otp: m_OTP.value};
        let response = await store.dispatch("login", cred);
        let accounts = response?.accounts ?? [];
        let singleDevCount = response?.singleDevCount ?? 0;
        let loginChoiceCount = accounts.length + (singleDevCount > 0 ? 1 : 0);
        if (loginChoiceCount > 1) {
            m_LoginToken.value = response?.token ?? "";
            m_Accounts.value = accounts;
            m_SingleDevCount.value = singleDevCount;
            schedulePendingLoginExpiration();
        }
    }
}

const hasLoginSelection = computed( () => {
  return m_Accounts.value.length + (m_SingleDevCount.value > 0 ? 1 : 0) > 1;
});

const isloggedin = computed( () => {
  return store.getters.isloggedin;
});

</script>
